import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const AlertContext = createContext({
  showAlert: () => {},
  showConfirm: async () => false,
});

function ToneIcon({ tone }) {
  if (tone === 'success') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="m9 12 2 2 4-4" />
      </svg>
    );
  }

  if (tone === 'error') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="m15 9-6 6" />
        <path d="m9 9 6 6" />
      </svg>
    );
  }

  if (tone === 'warning') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </svg>
  );
}

export function AlertProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    mode: 'alert',
    title: 'Notice',
    message: '',
    tone: 'info',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
  });
  const confirmResolverRef = useRef(null);

  const closeAlert = useCallback((confirmResult = false) => {
    if (state.mode === 'confirm' && confirmResolverRef.current) {
      confirmResolverRef.current(confirmResult);
      confirmResolverRef.current = null;
    }
    setState((prev) => ({ ...prev, open: false }));
  }, [state.mode]);

  const showAlert = useCallback((message, options = {}) => {
    setState({
      open: true,
      mode: 'alert',
      title: options.title || 'Notice',
      message: String(message || ''),
      tone: options.tone || 'info',
      confirmLabel: 'OK',
      cancelLabel: 'Cancel',
    });
  }, []);

  const showConfirm = useCallback((message, options = {}) => new Promise((resolve) => {
    confirmResolverRef.current = resolve;
    setState({
      open: true,
      mode: 'confirm',
      title: options.title || 'Confirm action',
      message: String(message || ''),
      tone: options.tone || 'warning',
      confirmLabel: options.confirmLabel || 'Confirm',
      cancelLabel: options.cancelLabel || 'Cancel',
    });
  }), []);

  useEffect(() => {
    if (!state.open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeAlert(false);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [state.open, closeAlert]);

  const value = useMemo(() => ({ showAlert, showConfirm }), [showAlert, showConfirm]);

  return (
    <AlertContext.Provider value={value}>
      {children}
      {state.open && (
        <div className="modal-overlay" onClick={closeAlert}>
          <div
            className={`modal app-alert app-alert--${state.tone}`}
            role={state.mode === 'confirm' ? 'dialog' : 'alertdialog'}
            aria-modal="true"
            aria-labelledby="app-alert-title"
            aria-describedby="app-alert-message"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="app-alert__head">
              <span className={`app-alert__tone app-alert__tone--${state.tone}`}>
                <ToneIcon tone={state.tone} />
              </span>
              <h3 id="app-alert-title">{state.title}</h3>
            </div>
            <p id="app-alert-message" className="app-alert__message">{state.message}</p>
            <div className="modal-actions">
              {state.mode === 'confirm' && (
                <button type="button" className="btn-secondary" onClick={() => closeAlert(false)}>{state.cancelLabel}</button>
              )}
              <button
                type="button"
                className={state.mode === 'confirm' ? 'btn-danger' : 'btn-primary'}
                onClick={() => closeAlert(true)}
              >
                {state.mode === 'confirm' ? state.confirmLabel : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AlertContext.Provider>
  );
}

export function useAlert() {
  return useContext(AlertContext);
}
